'use client';

import React, { useCallback, useState } from 'react';
import Button from '../button/button';
import { imageData } from '@/config/defaultBlur';
import { Col, Row } from 'react-bootstrap';
import {
  GetBlogLoadmorePosts,
  getNewsLoadmorePosts,
  getResourcesBlogPostForLoadMore,
} from '@/sanity/queries';
import {
  GetBlogLoadmorePostsResult,
  GetNewsLoadmorePostsResult,
  GetResourcesBlogPostForLoadMoreResult,
} from '@/sanity.types';
import { urlForImage } from '@/sanity/lib/utils';
import JsonLd from '../JsonLd';
import Link from 'next/link';
import Image from 'next/image';
import BlogArticle from '../blogArticle/blogArticle';
import { formatDate, summary } from '@/util';
import { PortableTextBlock } from 'next-sanity';
import BlogTile from '@/app/(frontend)/blog/component/blogTile/blogTile';

type sanityQuery =
  | typeof GetBlogLoadmorePosts
  | typeof getNewsLoadmorePosts
  | typeof getResourcesBlogPostForLoadMore;

type sanityQueryResult = NonNullable<
  GetBlogLoadmorePostsResult['posts'][0] &
    GetNewsLoadmorePostsResult['news'][0] &
    GetResourcesBlogPostForLoadMoreResult['resourcePosts'][0]
>;

interface GenericLoadMoreButtonProps {
  initialLastPostId?: string | null;
  resourceCategory?: string | null;
  excludeIds?: string[] | null;
  apiQuery: sanityQuery;
  type: 'blog' | 'news' | 'resource';
  limit?: number;
  lastEditedDate?: string | null;
  lastUpdatedDate?: string | null;
  reValidationTag?: string;
}

async function fetchPosts(featParams: {
  lastId: string | null;
  resourceCategory?: string | null;
  excludeIds?: string[] | null;
  apiQuery?: sanityQuery;
  limit: number;
  lastEditedDate?: string | null;
  lastUpdatedDate?: string | null;
  reValidationTag?: string;
}) {
  try {
    const {
      lastId,
      resourceCategory,
      excludeIds,
      apiQuery,
      limit,
      lastEditedDate,
      lastUpdatedDate,
      reValidationTag,
    } = featParams;
    const response = await fetch('/api/loadmore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: apiQuery,
        params: {
          category: resourceCategory,
          lastId,
          limit,
          excludeIds,
          lastEditedDate: lastEditedDate,
          updatedAt: lastUpdatedDate,
        },
        tag: reValidationTag,
      }),
    });
    const result = await response.json();
    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

export function GenericLoadMoreButton({
  initialLastPostId = null,
  excludeIds = null,
  resourceCategory = null,
  apiQuery,
  type,
  limit = 6,
  lastEditedDate,
  lastUpdatedDate,
  reValidationTag,
}: GenericLoadMoreButtonProps) {
  const [lastPostId, setLastPostId] = useState(initialLastPostId); // not usefull
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [posts, setPosts] = useState<sanityQueryResult[]>([]); // change type of this state
  const [error, setError] = useState<string | null>(null);
  const [blogDateInfo, setBlogDateInfo] = useState({
    editedDate: lastEditedDate,
    updatedAt: lastUpdatedDate,
  });

  const loadMorePosts = useCallback(async () => {
    if (isLoadingPosts) return;
    setIsLoadingPosts(true);
    setError(null);

    const { data: newPosts, error } = await fetchPosts({
      lastId: lastPostId,
      resourceCategory,
      excludeIds,
      apiQuery,
      limit,
      lastEditedDate: blogDateInfo.editedDate,
      lastUpdatedDate: blogDateInfo.updatedAt,
      reValidationTag,
    });

    if (error) {
      setError('Failed to load more posts. Please try again later.');
      setIsLoadingPosts(false);
      return;
    }
    if (!newPosts) {
      setHasMorePosts(false);
      setIsLoadingPosts(false);
      return;
    }
    if (type === 'blog') {
      const newBlogPosts = newPosts as GetBlogLoadmorePostsResult;
      setPosts(
        (prevPost) =>
          [...prevPost, ...newBlogPosts.posts] as sanityQueryResult[]
      );
      setBlogDateInfo({
        editedDate:
          newBlogPosts.posts[newBlogPosts.posts.length - 1]?.editedDate,
        updatedAt:
          newBlogPosts.posts[newBlogPosts.posts.length - 1]?._updatedAt,
      });
      setHasMorePosts(newBlogPosts.totalPosts > newBlogPosts.posts.length);
    }
    if (type === 'news') {
      const newNewsPosts = newPosts as GetNewsLoadmorePostsResult;
      setPosts(
        (prevPost) => [...prevPost, ...newNewsPosts.news] as sanityQueryResult[]
      );
      setBlogDateInfo({
        editedDate: newNewsPosts.news[newNewsPosts.news.length - 1]?.editedDate,
        updatedAt: newNewsPosts.news[newNewsPosts.news.length - 1]?._updatedAt,
      });
      setHasMorePosts(newNewsPosts.totalNews > newNewsPosts.news.length);
    }
    if (type === 'resource') {
      const newResourcePosts =
        newPosts as GetResourcesBlogPostForLoadMoreResult;
      setPosts(
        (prevPost) =>
          [
            ...prevPost,
            ...newResourcePosts.resourcePosts,
          ] as sanityQueryResult[]
      );
      setBlogDateInfo({
        editedDate:
          newResourcePosts.resourcePosts[
            newResourcePosts.resourcePosts.length - 1
          ]?.editedDate,
        updatedAt:
          newResourcePosts.resourcePosts[
            newResourcePosts.resourcePosts.length - 1
          ]?._updatedAt,
      });
      setHasMorePosts(
        newResourcePosts.totalResourcePost >
          newResourcePosts.resourcePosts.length
      );
    }

    setLastPostId(null);

    setIsLoadingPosts(false);
  }, [
    lastPostId,
    resourceCategory,
    excludeIds,
    isLoadingPosts,
    apiQuery,
    limit,
    type,
  ]);

  const renderPosts = () => {
    return (
      <Row className="g-4">
        {posts.map((post) => {
          const title = post.title;
          const blogImage =
            post?.coverMedia?.type == 'image'
              ? post?.coverMedia?.coverImage
              : post?.coverMedia?.thumbnail;
          const blogImageAlt =
            post?.coverMedia?.type == 'image'
              ? post?.coverMedia?.coverImage?.alt
              : post?.coverMedia?.thumbnail?.alt;

          switch (type) {
            case 'blog':
              return (
                <BlogTile
                  src={blogImage}
                  title={title}
                  alt={blogImageAlt || ''}
                  href={`/blog/${post.slug.current}`}
                  summary={summary(post?.content as PortableTextBlock[])}
                  key={post._id}
                  blurDataURL={
                    post?.coverMedia?.coverImage?.imgAsset?.metadata?.lqip ||
                    post?.coverMedia?.thumbnail?.imgAsset?.metadata?.lqip ||
                    imageData.blurImage
                  }
                />
              );
            case 'news':
              return (
                <Col xs={12} sm={6} md={4} key={post._id}>
                  <Link
                    href={`/news/${post.slug.current}`}
                    className="pressReleaseList"
                  >
                    <Image
                      src={
                        (post?.coverImage &&
                          (urlForImage(post?.coverImage)?.url() as string)) ||
                        '/n_images/default-blog.jpg'
                      }
                      alt={post?.coverImage?.alt || post.title}
                      width={300}
                      height={250}
                      quality={100}
                      sizes="(max-width: 767px) 100vw, (max-width: 1200px) 50vw, 30vw"
                      placeholder="blur"
                      blurDataURL={
                        post?.coverImage?.imgAsset?.metadata?.lqip ||
                        imageData.blurImage
                      }
                    />
                    <div className="date">
                      {post?.editedDate &&
                        formatDate(post?.editedDate as string)}
                    </div>
                    <h3>{post.title}</h3>
                  </Link>
                </Col>
              );
            case 'resource':
              return (
                <Col xs={12} md={12} lg={4} xl={4} key={post._id}>
                  <BlogArticle
                    resource={true}
                    resourceCategory={post?.category?.title}
                    src={blogImage}
                    alt={blogImageAlt || 'Resource image'}
                    blogTitle={post.title}
                    href={`/resources/${post.slug.current}`}
                    hrefCategorySlug={`/resources/category/${post?.category?.slug?.current}`}
                    blurDataURL={
                      post?.coverMedia?.coverImage?.imgAsset?.metadata?.lqip ||
                      post?.coverMedia?.thumbnail?.imgAsset?.metadata?.lqip ||
                      imageData.blurImage
                    }
                  />
                </Col>
              );
          }
        })}
      </Row>
    );
  };
  if (error) {
    return (
      <div className="text-center text-red-600 mt-4" role="alert">
        {error}
      </div>
    );
  }
  const jsonLD = () => {
    switch (type) {
      case 'blog':
        return (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: posts.map((post, index) => ({
                '@type': 'BlogPosting',
                position: index + 1,
                headline: post?.title,
                url: `${process.env.NEXT_PUBLIC_SITE_URL}/blog/${post?.slug?.current}`,
                description: summary(post?.content as PortableTextBlock[]),
                image: urlForImage(
                  post?.coverMedia?.coverImage
                )?.url() as string,
              })),
            }}
          />
        );
      case 'news':
        return (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              publisher: {
                '@type': 'Organization',
                name: 'SignUpGenius',
              },
              itemListElement: posts.map((post, index) => ({
                '@type': 'Article',
                position: index + 1,
                headline: post?.title,
                datePublished: post?.editedDate,
                url: `${process.env.NEXT_PUBLIC_SITE_URL}/news/${post?.slug?.current}`,
                image: urlForImage(post?.coverImage)?.url() || '',
              })),
            }}
          />
        );
      case 'resource':
        return (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: posts?.map((post, index) => ({
                '@type': 'CreativeWork',
                position: index + 1,
                headline: post?.title,
                url: `${process.env.NEXT_PUBLIC_SITE_URL}/resources/${post?.slug?.current}`,
                description: summary(post?.content as PortableTextBlock[]),
                image: urlForImage(
                  post?.coverMedia?.coverImage
                )?.url() as string,
              })),
            }}
          />
        );
    }
  };

  return (
    <section className="load-more-section">
      {jsonLD()}
      {posts.length > 0 && type == 'resource' && (
        <h2 className="more-article-title mb-6">More Articles</h2>
      )}
      <div className="blog-posts-grid">{renderPosts()}</div>

      {hasMorePosts && (
        <div className="text-center mt-4">
          <Button
            variant="primary"
            onClick={loadMorePosts}
            disabled={isLoadingPosts}
          >
            {isLoadingPosts ? (
              <span className="flex items-center gap-2">Loading...</span>
            ) : (
              'View More'
            )}
          </Button>
        </div>
      )}
    </section>
  );
}
